import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import { validarSize } from 'src/app/common/validation-utils';

export interface PolizaResponse {
  npoliza: string;
  dni: string;
  /** DNI completo tal como está en M094_CUERPOLIZA.DNI (tipo + número) — se reenvía sin reconstruir */
  dniCompleto: string | null;
  cdtipoIdentificacion: string | null;
  dstipoIdentificacion: string | null;
  dscomercial: string;
  /** CDPERSONA desde TPSC_PERSONAS (best-effort JOIN) — requerido por SP_CREAR_BENEF_CT */
  cdpersona: number | null;
  /**
   * M094_CUERPOLIZA.CDDELEGACION de la póliza. En modo línea de atención preselecciona
   * la oficina cuyo CDOFICINA coincide. Legacy: CumplimientoSinOcViewImpl
   * (BG_RETURN_FIELD línea 617; preselección líneas 804-812).
   */
  cddelegacion?: string | null;
}

export interface PolizaPageResponse {
  content: PolizaResponse[];
  totalElements: number;
  nextCursor?: string;
  previousCursor?: string;
  hasNext: boolean;
  hasPrevious: boolean;
}

export abstract class PolizaRepository {
  abstract buscar(
    npoliza: string | undefined,
    nombre: string | undefined,
    cursor: string | null,
    size: number,
    direction: 'NEXT' | 'PREVIOUS' | 'LAST'
  ): Promise<PolizaPageResponse>;
}

/**
 * Adaptador HTTP para búsqueda de pólizas de Sin Orientación de Servicio.
 *
 * Endpoint: GET /api/servicios/sin-oc/polizas/buscar
 * Tabla: M094_CUERPOLIZA (solo pólizas activas: FEBAJA > SYSDATE)
 * Paginación keyset/cursor (skill acceso-datos-oracle §7 — nunca OFFSET).
 *
 * Legacy: CumplimientoSinOcViewImpl.java línea 618
 */
@Injectable({ providedIn: 'root' })
export class HttpPolizaRepository extends PolizaRepository {

  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.backend.baseUrl}/servicios/sin-oc/polizas/buscar`;

  async buscar(
    npoliza: string | undefined,
    nombre: string | undefined,
    cursor: string | null,
    size: number,
    direction: 'NEXT' | 'PREVIOUS' | 'LAST'
  ): Promise<PolizaPageResponse> {
    validarSize(size);
    let params = new HttpParams()
      .set('size', String(size))
      .set('direction', direction);

    if (npoliza?.trim()) params = params.set('npoliza', npoliza.trim());
    if (nombre?.trim())  params = params.set('nombre',  nombre.trim());
    if (cursor) params = params.set('cursor', cursor);

    return firstValueFrom(
      this.http.get<PolizaPageResponse>(this.baseUrl, { params })
    );
  }
}
